/**
 * 2D rendering of H3 cells on a Google Map.
 * Maintains a polygon-per-cell cache; diffs incoming cell sets to add/update/remove.
 */

import { cellToPath } from './h3-utils';

interface OverlayState {
  cells: Map<string, google.maps.Polygon>;
  selectedCell: google.maps.Polygon | null;
}

const state: OverlayState = {
  cells: new Map(),
  selectedCell: null,
};

interface CellRenderOptions {
  /** Cell id → fill opacity 0..1. Absence = grid-only (no fill). */
  fills?: Map<string, number>;
}

/** Render the given H3 cells on the map, diffing against the previous render */
export function renderH3Overlay2D(
  map: google.maps.Map,
  cells: string[],
  options: CellRenderOptions = {},
): void {
  const next = new Set(cells);

  for (const [id, poly] of state.cells) {
    if (!next.has(id)) {
      poly.setMap(null);
      state.cells.delete(id);
    }
  }

  for (const cell of cells) {
    const fill = options.fills?.get(cell) ?? 0;
    const existing = state.cells.get(cell);
    if (existing) {
      existing.setOptions(polygonStyle(fill));
    } else {
      const poly = new google.maps.Polygon({
        map,
        paths: cellToPath(cell),
        clickable: false,
        ...polygonStyle(fill),
      });
      state.cells.set(cell, poly);
    }
  }
}

/** Highlight a single cell (for area-query click). Pass null to clear. */
export function highlightCell2D(
  map: google.maps.Map,
  cell: string | null,
): void {
  if (state.selectedCell) {
    state.selectedCell.setMap(null);
    state.selectedCell = null;
  }
  if (!cell) return;
  state.selectedCell = new google.maps.Polygon({
    map,
    paths: cellToPath(cell),
    clickable: false,
    strokeColor: '#ffd400',
    strokeOpacity: 0.95,
    strokeWeight: 3,
    fillColor: '#ffd400',
    fillOpacity: 0.18,
    zIndex: 10,
  });
}

/** Tear down every rendered polygon */
export function clearH3Overlay2D(): void {
  for (const poly of state.cells.values()) poly.setMap(null);
  state.cells.clear();
  if (state.selectedCell) {
    state.selectedCell.setMap(null);
    state.selectedCell = null;
  }
}

function polygonStyle(fillOpacity: number): Partial<google.maps.PolygonOptions> {
  return {
    strokeColor: '#5fa3ff',
    strokeOpacity: 0.55,
    strokeWeight: 1,
    fillColor: '#ff5b3a',
    fillOpacity,
  };
}
